import type { PositionedNode } from '../renderers/canvas-layers/GraphCanvasLayer';
import type { JourneyNode } from '../../../core/entities/node';
import type { GraphCamera } from './graph-camera';

export interface GraphPointer {
  x: number;
  y: number;
}

export const BASE_NODE_RADIUS = 8;
export const FOCUSED_NODE_RADIUS = 18;
export const NODE_HIT_RADIUS = 20;

export interface GraphBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function layoutNodesForViewport(
  nodes: JourneyNode[],
  width: number,
  height: number,
  nodeOffsets: Record<string, { x: number; y: number }> = {},
  anchoredNodeIds: ReadonlySet<string> = new Set()
): PositionedNode[] {
  const centerX = width / 2;
  const centerY = height / 2;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const baseRadius = Math.max(120, Math.min(width, height) * 0.12);
  const radialStep = Math.max(42, Math.min(width, height) * 0.075);

  return nodes.map((node, index) => ({
    ...node,
    ...organicPositionForNode(
      node,
      index,
      centerX,
      centerY,
      baseRadius,
      radialStep,
      nodeOffsets[node.nodeId],
      anchoredNodeIds.has(node.nodeId)
    ),
  }));
}

export function pickNodeAtPoint(
  nodes: PositionedNode[],
  pointer: GraphPointer,
  hitRadius = NODE_HIT_RADIUS
): PositionedNode | null {
  let candidate: PositionedNode | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const node of nodes) {
    const dx = pointer.x - node.x;
    const dy = pointer.y - node.y;
    const distanceSquared = dx * dx + dy * dy;
    const effectiveHitRadius = hitRadius * (node.depth ?? 1);

    if (distanceSquared > effectiveHitRadius * effectiveHitRadius || distanceSquared >= bestDistance) {
      continue;
    }

    candidate = node;
    bestDistance = distanceSquared;
  }

  return candidate;
}

export function nodeRadius(nodeId: string, focusedNodeId: string | null): number {
  return nodeId === focusedNodeId ? FOCUSED_NODE_RADIUS : BASE_NODE_RADIUS;
}

export function worldPointFromCanvasPoint(pointer: GraphPointer, camera: GraphCamera): GraphPointer {
  return {
    x: (pointer.x - camera.x) / camera.scale,
    y: (pointer.y - camera.y) / camera.scale,
  };
}

export function measureLayoutBounds(nodes: PositionedNode[]): GraphBounds {
  if (nodes.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  return nodes.reduce<GraphBounds>((bounds, node) => {
    const radius = nodeRadius(node.nodeId, null) * (node.depth ?? 1.05) * 1.3;
    return {
      minX: Math.min(bounds.minX, node.x - radius),
      maxX: Math.max(bounds.maxX, node.x + radius),
      minY: Math.min(bounds.minY, node.y - radius),
      maxY: Math.max(bounds.maxY, node.y + radius),
    };
  }, {
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  });
}

export function applyAmbientDrift(nodes: PositionedNode[], timeMs: number): PositionedNode[] {
  const time = timeMs / 1000;

  return nodes.map((node, index) => {
    const depth = node.depth ?? 1;
    const seed = hashNode(node.nodeId) + index * 17;
    const amplitude = 3 + depth * 6;
    const speed = 0.24 + depth * 0.12;
    const phase = (seed % 360) * (Math.PI / 180);
    const orbitX = Math.sin(time * speed + phase) * amplitude;
    const orbitY = Math.cos(time * (speed * 0.8) + phase * 1.2) * amplitude * 0.72;

    return {
      ...node,
      x: node.x + orbitX,
      y: node.y + orbitY,
    };
  });
}

function organicPositionForNode(
  node: JourneyNode,
  index: number,
  centerX: number,
  centerY: number,
  baseRadius: number,
  radialStep: number,
  offset?: { x: number; y: number },
  isAnchored = false
): Pick<PositionedNode, 'x' | 'y' | 'depth'> {
  const seed = hashNode(node.nodeId);
  const typeLane = node.type === 'Human' ? -0.34 : node.type === 'Project' ? 0.18 : 0.62;
  const depth = 0.78 + (seed % 6) * 0.085;

  if (isAnchored && offset) {
    return {
      x: centerX + offset.x,
      y: centerY + offset.y,
      depth,
    };
  }

  const orbit = baseRadius + Math.sqrt(index + 1) * radialStep * (0.88 + depth * 0.22);
  const angle = index * (Math.PI * (3 - Math.sqrt(5))) + typeLane + (seed % 19) * 0.03;
  const wobble = ((seed % 23) - 11) * 4;

  return {
    x: centerX + Math.cos(angle) * orbit + Math.sin(angle * 1.6) * wobble + (offset?.x ?? 0),
    y: centerY + Math.sin(angle) * orbit * 0.72 + Math.cos(angle * 1.35) * wobble * 0.8 + (offset?.y ?? 0),
    depth,
  };
}

function hashNode(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
}
